import type { Cue } from "subtitletools";
import type { EditDoc } from "./model";

/**
 * Editor session persistence in IndexedDB: the `EditDoc` (cues/style/trim) plus
 * light file metadata, keyed by a file fingerprint. The video bytes are *not*
 * stored (could be huge) — restoring re-uses the doc when the same file is
 * re-dropped (fingerprint match). All calls degrade gracefully if IndexedDB is
 * unavailable (private mode, etc.). IndexedDB (not localStorage) so a future
 * File System Access handle — only structured-cloneable into IDB — can join it.
 */

const DB_NAME = "teksta-subtitle-editor";
const STORE = "sessions";
const RECENT_LIMIT = 12;
const TEKSTA_RUN_SESSION_PREFIX = "teksta-run:";

export interface SessionMeta {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  duration: number;
}

export interface SavedSession {
  fp: string;
  meta: SessionMeta;
  doc: EditDoc;
  /** The cues as first parsed from the subtitle file, for "Revert edits". */
  originalCues?: Cue[];
  savedAt: number;
}

/** Stable identity for a dropped file (available before metadata loads). */
export function fingerprint(file: File): string {
  return `${file.name}__${file.size}__${file.lastModified}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "fp" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDb();
  try {
    return await promisify(run(db.transaction(STORE, mode).objectStore(STORE)));
  } finally {
    db.close();
  }
}

export async function listSessions(): Promise<SavedSession[]> {
  try {
    const all = await withStore<SavedSession[]>("readonly", (s) => s.getAll());
    return all
      .filter((session) => !session.fp.startsWith(TEKSTA_RUN_SESSION_PREFIX))
      .sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

export async function loadSession(fp: string): Promise<SavedSession | undefined> {
  try {
    return await withStore<SavedSession | undefined>("readonly", (s) => s.get(fp));
  } catch {
    return undefined;
  }
}

export async function deleteSession(fp: string): Promise<void> {
  try {
    await withStore("readwrite", (s) => s.delete(fp));
  } catch {
    // ignore
  }
}

let persistRequested = false;
/**
 * Ask the browser to mark our storage **persistent** (exempt from best-effort
 * eviction). Per the Storage spec this should be requested when saving real user
 * data — call it once a session has actual edits, not on page load. Chromium/
 * Safari decide silently from engagement; Firefox may prompt. Best-effort, once.
 */
export async function ensurePersistentStorage(): Promise<void> {
  if (persistRequested || !navigator.storage?.persist) return;
  persistRequested = true;
  try {
    await navigator.storage.persist();
  } catch {
    // ignore
  }
}

export async function saveSession(session: SavedSession): Promise<void> {
  try {
    await withStore("readwrite", (s) => s.put(session));
    const all = await listSessions();
    if (all.length > RECENT_LIMIT) {
      await Promise.all(all.slice(RECENT_LIMIT).map((old) => deleteSession(old.fp)));
    }
  } catch {
    // ignore
  }
}
